import React, { useEffect, useState } from "react";
import {
  useParams,
  Link
} from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  User,
  AlertCircle,
  History
} from "lucide-react";
import * as ownerApi from "../../api/payroll/salaryStructureApi";
import * as portalApi from "../../api/payroll/salaryStructurePortalApi";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function SalaryStructureByStaff({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/salary-structures" : "/salary-structures";
  const { staffId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getSalaryStructureByStaff(staffId);
        setItems(Array.isArray(res) ? res : res.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load salary history");
      } finally {
        setLoading(false);
      }
    })();
  }, [staffId, isPortal]);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <Link
          to={basePath}
          className="-ml-2 mb-2 inline-flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to Salary Structures
        </Link>
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          Salary History
        </h1>
        <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
          Historical structure revisions and component breakdowns for this staff member
        </p>
      </div>

      

      {loading ? (
        <div className="space-y-4">
          <Skeleton height={140} width="100%" />
          <Skeleton height={140} width="100%" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={History}
              title="No structure records found"
              description="There are no historical or active salary structures configured for this staff member."
              action={
                <Link
                  to={basePath}
                  className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Return to structures list
                </Link>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item._id} className="overflow-hidden">
              <CardHeader className="flex flex-col gap-2 bg-neutral-50/60 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                    <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                      {item.staffId?.name || item.staffName || "Staff Member"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                    <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>
                      Effective:{" "}
                      {item.effectiveFrom
                        ? new Date(item.effectiveFrom).toLocaleDateString()
                        : "Immediate"}
                    </span>
                  </div>
                </div>

                <div className="text-left sm:text-right">
                  <span className="block text-xs font-semibold uppercase text-neutral-500 dark:text-neutral-400">
                    Gross Monthly Pay
                  </span>
                  <span className="font-mono text-xl font-bold text-success-700">
                    ₹{Number(item.grossMonthly || 0).toLocaleString()}
                  </span>
                </div>
              </CardHeader>

              <CardContent>
                <h4 className="mb-3 text-xs font-semibold uppercase text-neutral-500 dark:text-neutral-400">
                  Component Breakdown
                </h4>
                <div className="divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden rounded border border-neutral-200 dark:border-neutral-800">
                  {(item.components || []).length > 0 ? (
                    item.components.map((c, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      >
                        <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                          {c.salaryComponentId?.name || c.name || "Component"}
                        </span>
                        <span className="font-mono text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                          ₹{Number(c.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                      No itemized components defined.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}